/**
 * Centralized API URL configuration.
 *
 * In production, uses the api.explainit.hu custom domain. It is a subdomain of
 * the site's registrable domain (explainit.hu), so the httpOnly session cookie
 * is FIRST-PARTY. The previous *.azurecontainerapps.io host was a different site,
 * which made the cookie third-party — and iOS/Safari (WebKit ITP) blocks
 * third-party cookies, silently breaking mobile login. See ADR-0024.
 * In development, falls back to localhost:8000 (the backend's default port, per
 * .env.example / vite.config.js). The documented local workflow overrides this via
 * VITE_API_URL — see CLAUDE.md "Local Test Environment" (Vite :5180 + FastAPI :8765,
 * to avoid Docker port collisions on :3000/:8000). The unset-env unit test therefore
 * pins :8000 (the actual code fallback), not the machine-specific override.
 * Can be overridden via the VITE_API_URL environment variable. Whatever the source,
 * the resolved value is normalized — surrounding whitespace trimmed and trailing
 * slash(es) stripped — so the no-trailing-slash invariant always holds: callers
 * concatenate `${API_URL}${path}` with a leading-slash path, so a stray trailing
 * slash (e.g. the build var VITE_API_URL set to "https://api.explainit.hu/") would
 * otherwise produce "//api/...".
 *
 * TECH DEBT (Issue #139; see also ADR-0024, Alternative 1): When the SWA linked
 * backend is configured, resolve to a same-origin relative base instead
 * (`import.meta.env.VITE_API_URL || ''`), keeping the normalization below. All API
 * calls would then use same-origin relative URLs via the SWA reverse proxy, which
 * also lets the session cookie drop to SameSite=Lax.
 */
const DEFAULT_API_URL = import.meta.env.PROD ? 'https://api.explainit.hu' : 'http://localhost:8000';

// Normalize a configured override — trim whitespace and strip trailing slash(es) —
// BEFORE the fallback, so an override that is absent (undefined), whitespace-only, or
// pure-slash — all falsy after normalization — falls back to the default instead of
// yielding an empty or "//api/..."-producing base. API_URL is therefore always a
// non-empty base URL with no trailing slash, which callers rely on for
// `${API_URL}${path}`.
const normalizedOverride = import.meta.env.VITE_API_URL?.trim().replace(/\/+$/, '');

export const API_URL = normalizedOverride || DEFAULT_API_URL;
